import { inject, observer } from "mobx-react";
import BlockKitStore from "../stores/BlockKitStore";
import React, { useEffect, useState, useRef } from "react";
import { BlockJsonModel, LanguageJsonModel } from "../models/BlockTransportSystemJsonModel";
import { useParams } from "react-router-dom";
import { AllCommunityModule, ICellRendererParams, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react'; 
import GlobalEnvironments from "../constants/GlobalEnvironments";
import { getLocaleString } from "../utils/DateHelper";
import BlockEditModal from "../components/BlockEditModal/BlockEditModal";
import { Button, Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";

ModuleRegistry.registerModules([AllCommunityModule]);

const ImageCell = (props: ICellRendererParams) => {
    const imgSrc = `${GlobalEnvironments.getBlockTransportSystemHostUrlForWeb()}${props.value}`;
    return props.value ? (
    <a href={imgSrc} target="_blank" rel="noopener noreferrer">
        <img
        src={imgSrc}
        alt="thumbnail"
        style={{ width: "100%", height: "100%", objectFit: 'contain' }}
        />
    </a>
    ) : null;
};

interface Props {
    blockKitStore?: BlockKitStore;
}

const PublishedBlockPage: React.FC<Props> = inject("blockKitStore")(observer(({blockKitStore}) => {
    const [blocks, setBlocks] = useState<BlockJsonModel[]>();
    const [selectedBlock, setSelectedBlock] = useState<BlockJsonModel>();
    const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>();
    const { categoryId } = useParams();
    const gridRef = useRef<AgGridReact<BlockJsonModel>>(null);
    const { t } = useTranslation();

    const fetchBlocksAsync = (async () => {
        const response = await blockKitStore?.studioController.getCategoryBlocksAsync(categoryId!);
        setBlocks(response);
       });

    useEffect(() => {
        gridRef.current?.api?.refreshCells({ force: true });
    }, [blockKitStore?.timeZone]);

    useEffect(() => {
       fetchBlocksAsync();
    }, []);

    const EditButtonCell: React.FC<ICellRendererParams> = (params) => {
        return (
            <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                    setSelectedBlock(params.data);
                    setIsOpenEditModal(true);
                }}>
                    {t("button-edit")}
            </Button>
        );
    }

    const DeleteButtonCell: React.FC<ICellRendererParams> = (params) => {
        return (
            <Button
                variant="danger"
                size="sm"
                onClick={async () => {
                    if (!confirm(`${t("delete-confirmation-message")}\n${t("delete-warning-message")}`)) {
                        return;
                    }

                    const response = await blockKitStore?.studioController.deletePublishedBlockAsync(params.data);
                    if (response?.isSuccess) {
                        await fetchBlocksAsync();
                        alert(t("delete-success"));
                    } else {
                        alert(`${t("delete-failed")}  (${response?.message})`);
                    }
                }}>
                    {t("button-delete")}
            </Button>
        );
    }

    return (
        <Container fluid>
            {isOpenEditModal &&
                <BlockEditModal
                    isShow={isOpenEditModal}
                    selectedBlock={selectedBlock}
                    onClickApply={async (block: BlockJsonModel) => {
                        if (!confirm(t("edit-confirmation-message"))) {
                            return;
                        }

                        const response = await blockKitStore?.studioController.postCategoryBlockAsync(block);
                        if (response?.isSuccess) {
                            await fetchBlocksAsync();
                            setIsOpenEditModal(false);
                            alert(t("edit-success"));
                        } else {
                            alert(`${t("edit-failed")} (${response?.message})`);
                        }
                    }}
                    onClickClose={() => {
                        setIsOpenEditModal(false);
                    }}
                />
            }
            <style>{`
                .ag-header-cell-label {
                    justify-content: center;
                }
            `}</style>
            <h4 className="m-2 pt-3 fw-bold">{t("published-design")}</h4>
            <div style={{ height: "80vh"}}>
                <AgGridReact<BlockJsonModel>
                    ref={gridRef}
                    getRowId={params => params.data.id}
                    rowData={blocks}
                    defaultColDef={{
                        cellStyle: { textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" }
                    }}
                    columnDefs={[ 
                    {
                        headerName: t("delete"),
                        width: 80,
                        cellRenderer: DeleteButtonCell,
                    },
                    {
                        headerName: t("origin"),
                        field: "originUrl",
                        width: 90,
                        cellRenderer: ImageCell,
                    },
                    {
                        headerName: t("preview"),
                        field: "previewUrl",
                        width: 90,
                        cellRenderer: ImageCell,
                    },
                    {
                        headerName: t("icon"),
                        field: "iconUrl",
                        width: 90,
                        cellRenderer: ImageCell,
                    },
                    {
                        headerName: t("name"),
                        field: "name",
                        filter: true,
                    },
                    {
                        headerName: t("description"),
                        field: "description",
                        filter: true,
                    },
                    {
                        headerName: t("license"),
                        field: "license",
                        filter: true,
                    },
                    {
                        headerName: t("default-price"),
                        field: "price",
                        width: 130,
                        filter: true,
                        editable: true
                    },
                    {
                        headerName: t("price-by-country"),
                        autoHeight: true,
                        filter: true,
                        cellStyle: {
                            "whiteSpaceCollapse": "preserve-breaks",
                            "lineHeight": 1.7
                        },
                        valueGetter: params => params.data?.prices?.map(i => `${i.locale}(${i.price})`).join("\n")
                    },
                    {
                        headerName: t("edit"),
                        width: 80,
                        cellRenderer: EditButtonCell,
                    },
                    {
                        headerName: t("priority"),
                        field: "priority",
                        width: 120,
                        filter: true,
                    },
                    {
                        headerName: t("dedicated-product"),
                        field: "dedicatedProducts",
                        width: 180,
                        autoHeight: true,
                        filter: true,
                        cellStyle: {
                            "whiteSpaceCollapse": "preserve-breaks",
                            "lineHeight": 1.7
                        },
                        valueGetter: (params) => {
                            const arr = params.data?.dedicatedProducts ?? [];
                            return Array.isArray(arr) ? arr.sort().join("\n") : arr;
                        }
                    },
                    {
                        headerName: t("block-type"),
                        field: "type",
                        filter: true,
                    },
                    {
                        headerName: t("layout"),
                        field: "layoutType",
                        filter: true,
                    },
                    {
                        headerName: t("text-aligh"),
                        field: "textAlignType",
                        filter: true,
                    },
                    {
                        headerName: t("image-direction"),
                        field: "imageDirection",
                        filter: true,
                    },
                    {
                        headerName: t("media-size"),
                        field: "mediaSize",
                        filter: true,
                    },
                    {
                        headerName: t("id"),
                        field: "id",
                        filter: true
                    },
                    {
                        headerName: t("release-date"),
                        field: "releaseDate",
                        filter: "agDateColumnFilter",
                        valueFormatter: parmas => getLocaleString(new Date(parmas.data.releaseDate), blockKitStore?.timeZone),
                    },
                    {
                        headerName: t("expire-date"),
                        field: "expireDate",
                        filter: "agDateColumnFilter",
                        valueFormatter: params => getLocaleString(new Date(params.data.expireDate), blockKitStore?.timeZone)
                    },
                    ]}
                    rowSelection={"single"}
                    getRowStyle={params => {
                        const expireDate = new Date(params.data.expireDate);
                        const now = new Date();
                        if (expireDate < now) {
                            return { backgroundColor: "#ffe0e0" };
                        }
                        return null;
                        }
                    }
                />
            </div>
        </Container>
    )
}));

export default PublishedBlockPage;